<template>
  <Layout>
    <!-- SEO Meta Component -->
    <SeoMeta
      :title="getSeoTitle"
      :description="getSeoDescription"
      :keywords="getSeoKeywords"
      :canonical="`/tool/${tool.slug}`"
      pageType="tool"
      :faqs="dynamicToolFaqs"
      :breadcrumbs="[
        { label: 'Home', url: '/' },
        { label: 'Tools', url: '/tools' },
        { label: tool.name, url: `/tool/${tool.slug}` }
      ]"
      :product="{
        '@type': 'SoftwareApplication',
        '@id': `https://www.balan.ai/tool/${tool.slug}`,
        name: tool.name,
        description: tool.short_description,
        image: getToolImage,
        url: `/tool/${tool.slug}`,
        applicationCategory: 'Artificial Intelligence',
        applicationSubCategory: tool.use_cases?.[0]?.name || 'AI Tool',
        operatingSystem: tool.platforms?.map(p => p.name).join(', ') || 'Web',
        offers: {
          '@type': 'Offer',
          availability: 'https://schema.org/OnlineOnly',
          price: tool.pricing_model === 'free' ? '0' : 'varies',
          priceCurrency: 'USD',
          pricingDetails: tool.pricing_model
        },
        features: tool.features?.map(feature => ({
          '@type': 'FeatureSpecification',
          'name': feature.name
        })) || [],
        potentialAction: {
          '@type': 'UseAction',
          'target': `/tool/${tool.slug}`
        },
        category: [
          ...tool.tags?.map(tag => tag.name) || [],
          ...tool.use_cases?.map(useCase => useCase.name) || []
        ].join(', '),
        datePublished: tool.created_at,
        dateModified: tool.updated_at
      }"
    />

    <main class="min-h-screen">
      <!-- Header is critical for user navigation - load immediately -->
      <ToolHeader :tool="tool" />
          
      <!-- Hero Section - critical for first impression -->
      <ToolHero :tool="tool" />

      <!-- Features Section -->
      <div id="features-section" class="max-w-7x py-12 relative">       
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Tool Features -->
          <ToolFeatures :tool="tool" />

          <!-- FAQs Section -->
          <ToolFaqs :tool="tool" />
        </div>
      </div>

      <!-- Similar Tools - Kept in main thread but not critical -->
      <ToolSimilar :relatedTools="relatedToolsList" :isLoading="loadingSimilarTools" />

      <!-- Newsletter Section - Now lazy loaded with visibility threshold -->
      <LazyRenderer 
        :visibilityBased="true" 
        :threshold="0.2" 
        :rootMargin="'200px'"
      >
        <ToolNewsletterSubscription :tool="tool" />
      </LazyRenderer>
    </main>
  </Layout>
</template>

<script setup>
import { ref, computed, onMounted, defineAsyncComponent } from 'vue';
import Layout from '@/Layouts/Layout.vue';
import { Icon } from '@iconify/vue';
import SeoMeta from '@/Components/SeoMeta.vue';
import ToolHeader from '@/Pages/Tool/ToolHeader.vue';
import ToolHero from '@/Pages/Tool/ToolHero.vue';
import ToolFeatures from '@/Pages/Tool/ToolFeatures.vue';
import ToolFaqs from '@/Pages/Tool/ToolFaqs.vue';
import ToolSimilar from '@/Pages/Tool/ToolSimilar.vue';
import LazyRenderer from '@/Pages/Tool/LazyRenderer.vue';
import FavoriteButton from '@/Components/FavoriteButton.vue';
import axios from 'axios';

// Lazy-load the newsletter component
const ToolNewsletterSubscription = defineAsyncComponent(() => 
  import('@/Pages/Tool/ToolNewsletterSubscription.vue')
);

const props = defineProps({
  tool: {
    type: Object,
    required: true,
  },
  relatedTools: {
    type: Array,
    required: true,
  },
});

// Define reactive variables for similar tools
const relatedToolsList = ref(props.relatedTools);
const loadingSimilarTools = ref(props.relatedTools.length === 0);

// SEO Computed Properties
const getSeoTitle = computed(() => {
  if (!props.tool) return 'Loading...';
  const baseTitle = props.tool.meta_title || `${props.tool.name} - AI Tool`;
  const category = props.tool.use_cases?.[0]?.name;
  return category ? `${baseTitle} for ${category}` : `${baseTitle}`;
});

const getSeoDescription = computed(() => {
  if (props.tool.meta_description) return props.tool.meta_description;
  
  const category = props.tool.use_cases?.[0]?.name;
  const baseDesc = `${props.tool.short_description}`;
  return category 
    ? `${baseDesc} Learn how ${props.tool.name} can enhance your ${category.toLowerCase()} workflow.`
    : `${baseDesc} Explore features and capabilities on Balan.ai.`;
});

const getSeoKeywords = computed(() => {
  const baseKeywords = [
    props.tool.name.toLowerCase(),
    'ai tool',
    'artificial intelligence'
  ];
  
  const tagKeywords = props.tool.tags?.map(tag => tag.name.toLowerCase()) || [];
  const useCaseKeywords = props.tool.use_cases?.map(uc => uc.name.toLowerCase()) || [];
  const platformKeywords = props.tool.platforms?.map(p => `${p.name.toLowerCase()} ai tool`) || [];
  const featureKeywords = props.tool.features?.map(f => f.name.toLowerCase()) || [];
  
  return [...new Set([
    ...baseKeywords,
    ...tagKeywords,
    ...useCaseKeywords,
    ...platformKeywords,
    ...featureKeywords
  ])].join(', ');
});

// Image handling
const getToolImage = computed(() => {
  return props.tool.media?.[0]?.original_url || '/storage/no-logo.png';
});

// Load similar tools if not provided on initial render
onMounted(() => {
  // If no related tools were provided, fetch them asynchronously
  if (props.relatedTools.length === 0 && props.tool.id) {
    loadingSimilarTools.value = true;
    
    axios.get(`/api/tools/${props.tool.id}/similar`)
      .then(response => {
        // Update the similar tools list
        relatedToolsList.value = response.data;
      })
      .catch(error => {
        console.error('Error loading similar tools:', error);
      })
      .finally(() => {
        loadingSimilarTools.value = false;
      });
  }
  
  // Check if the browser supports IntersectionObserver (for lazy loading)
  if (!('IntersectionObserver' in window)) {
    // For older browsers without IntersectionObserver, load components immediately
    console.log('IntersectionObserver not supported, loading components eagerly');
  }
});

// Generate dynamic FAQs from tool data
const dynamicToolFaqs = computed(() => {
  const faqs = [];

  // Example: Add FAQs from tool features
  if (props.tool.features && props.tool.features.length) {
    props.tool.features.slice(0, 5).forEach(feature => {
      faqs.push({
        question: `What is the ${feature.name} feature?`,
        answer: feature.pivot?.details || `${feature.name} is a key feature of ${props.tool.name} that enhances your workflow.`
      });
    });
  }

  // Add platform-related FAQ
  if (props.tool.platforms && props.tool.platforms.length) {
    faqs.push({
      question: `What platforms is ${props.tool.name} available on?`,
      answer: `${props.tool.name} is available on: ${props.tool.platforms.map(p => p.name).join(', ')}`
    });
  }

  // Add pricing FAQ
  faqs.push({
    question: `What is the pricing model for ${props.tool.name}?`,
    answer: props.tool.pricing_model 
      ? `${props.tool.name} offers a ${props.tool.pricing_model} pricing model.`
      : `Pricing details for ${props.tool.name} can be found on their website.`
  });

  // Add use case FAQ
  if (props.tool.use_cases && props.tool.use_cases.length) {
    faqs.push({
      question: `What categories does ${props.tool.name} cover?`,
      answer: `${props.tool.name} is primarily used in: ${props.tool.use_cases.map(uc => uc.name).join(', ')}`
    });
  }
  return faqs;
});
</script>

<style scoped>
/* Ensure proper hover states and transitions */
.hover\:bg-gray-800\/50:hover {
  background-color: rgb(31 41 55 / 0.5);
}

.bg-gray-900\/50 {
  background-color: rgb(17 24 39 / 0.5);
}

/* Prose styling for content */
.prose-invert {
  --tw-prose-invert-body: theme('colors.gray.300');
  --tw-prose-invert-headings: theme('colors.white');
  --tw-prose-invert-links: theme('colors.blue.400');
}

/* Ensure smooth transitions */
.transition-all {
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Add a fade-in animation on mount */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

header {
  animation: fadeIn 0.5s ease-out;
}

.card-style {
  background: 
    radial-gradient(
      ellipse at center, 
      rgba(30, 41, 59, 0.5) 0%, 
      rgba(17, 24, 39, 0.7) 100%
    );
  backdrop-filter: blur(2px);
}

/* Media queries to reduce animations on mobile devices */
@media (max-width: 768px) {
  /* Reduce animations for mobile devices */
  .card-style {
    backdrop-filter: none; /* Remove blur filter on mobile */
  }
  
  /* Disable animations for low-powered devices */
  @media (prefers-reduced-motion: reduce) {
    *, ::before, ::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
}
</style>